import React from "react";
import {
  Bike,
  Phone,
  Clock,
  ShieldCheck,
  Wrench,
} from "lucide-react";
import { heroImage, siteInfo } from "../mock/mock";

const Hero = () => {
  const telHref = `tel:${siteInfo.phone.replace(/\s/g, "")}`;

  const chips = [
    { icon: ShieldCheck, label: "Well Maintained" },
    { icon: Wrench, label: "Regularly Serviced" },
    { icon: Bike, label: "Flexible KM Plans" },
  ];

  return (
    <section id="home" className="relative">
      {/* Hero image with overlay */}
      <div className="relative h-[600px] sm:h-[640px] lg:h-[720px] w-full overflow-hidden">
        <img
          src={heroImage}
          alt="Scooter rental in Udupi and Manipal by RR Bros Bike Rental"
          className="absolute inset-0 w-full h-full object-cover scale-105"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/25" />

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto h-full px-4 sm:px-6 lg:px-8 flex items-center">
          <div className="max-w-2xl animate-fade-in-up">
            <span className="inline-block text-orange-400 font-semibold text-sm uppercase tracking-[0.2em] mb-3">
              RR Bros &bull; Udupi &bull; Manipal
            </span>
            {/* Screen-reader friendly H1 phrase with primary keywords */}
            <span className="sr-only">
              Bike Rental in Udupi and Scooty Rental in Manipal — self drive
              two wheeler rental by RR Bros Bike Rental
            </span>
            <h1 className="text-white font-black text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
              Your Scooter
              <br />
              When You Need
              <br />
              <span className="text-orange-500">It Most</span>
            </h1>
            <p className="mt-6 text-neutral-200 text-base sm:text-lg max-w-lg leading-relaxed">
              Affordable scooter rentals in Udupi &amp; Manipal. Well maintained
              fleet and flexible kilometer plans that suit every rider.
            </p>

            {/* Benefit chips */}
            <div className="mt-6 flex flex-wrap gap-2">
              {chips.map((c, i) => {
                const Icon = c.icon;
                return (
                  <div
                    key={i}
                    className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur border border-white/20 text-white text-xs sm:text-sm font-semibold px-3 py-1.5 rounded-full"
                  >
                    <Icon className="w-3.5 h-3.5 text-orange-400" />
                    {c.label}
                  </div>
                );
              })}
            </div>

            {/* CTAs */}
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a
                href="#bikes"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm uppercase tracking-wider shadow-lg hover:shadow-orange-300/40 transition-all"
              >
                <Bike className="w-4 h-4" />
                View Scooters
              </a>
              <a
                href={telHref}
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white/10 backdrop-blur border border-white/30 hover:bg-white/20 text-white font-bold text-sm uppercase tracking-wider transition-all"
              >
                <Phone className="w-4 h-4" />
                Call Us
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Quick info strip */}
      <div className="relative -mt-10 z-20 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-2xl shadow-xl border border-neutral-100 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-neutral-100 overflow-hidden">
          {[
            {
              icon: Bike,
              title: "Popular Scooters",
              desc: "Activa 6G, Fascino, Ray-Z",
            },
            {
              icon: Clock,
              title: "Quick Pickup",
              desc: "Fast paperwork, ride out in minutes",
            },
            {
              icon: ShieldCheck,
              title: "Well Maintained",
              desc: "Regularly serviced at authorised centres",
            },
          ].map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="flex items-center gap-4 p-5 sm:p-6">
                <div className="w-12 h-12 rounded-2xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-6 h-6 text-orange-500" />
                </div>
                <div>
                  <p className="font-black text-neutral-900 leading-tight">
                    {s.title}
                  </p>
                  <p className="text-xs text-neutral-500 mt-0.5">{s.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Hero;
